import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../AuthContext';
import {
    Card,
    CardBody,
    Typography,
    Button,
    Input,
} from '@material-tailwind/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExport } from '@fortawesome/free-solid-svg-icons';
import PartsTransactionForm from './PartsTransactionForm';
const { rootUrl } = require('../../../config/config');

const PartsStockCard = ({ partId, partName }) => {
    const { token } = useAuth();
    const authToken = token || localStorage.getItem('authToken');

    const [stockCard, setStockCard] = useState([]);
    const [part, setPart] = useState(null);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({
        date_from: '',
        date_to: ''
    });
    const [showTransactionForm, setShowTransactionForm] = useState(false);

    const fetchStockCard = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filters.date_from) params.append('date_from', filters.date_from);
            if (filters.date_to) params.append('date_to', filters.date_to);

            const response = await axios.get(
                `${rootUrl}/api/parts/${partId}/stock-card?${params.toString()}`,
                { headers: { 'Authorization': `Bearer ${authToken}` } }
            );

            if (response.data && response.data.success) {
                setStockCard(response.data.data || []);
                setPart(response.data.part);
                setSummary(response.data.summary);
            }
        } catch (error) {
            console.error('Error fetching stock card:', error);
        } finally {
            setLoading(false);
        }
    }, [partId, filters, authToken]);

    useEffect(() => {
        if (partId) {
            fetchStockCard();
        }
    }, [partId, filters, fetchStockCard]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES'
        }).format(amount || 0);
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('en-KE');
    };

    const getTransactionTypeLabel = (type) => {
        const types = {
            'PUR': 'Purchase',
            'SAL': 'Sale',
            'TRF_IN': 'Transfer In',
            'TRF_OUT': 'Transfer Out',
            'ADJ': 'Adjustment',
            'USE': 'Usage',
            'REV': 'Reversal'
        };
        return types[type] || type;
    };

    return (
        <div className="space-y-4">
            <Card>
                <CardBody>
                    <div className="flex justify-between items-center mb-4">
                        <div>
                            <Typography variant="h5">{partName || part?.part_name} - Stock Card</Typography>
                            <Typography variant="small" color="gray">
                                Part Number: {part?.part_number || '-'} | 
                                Current Quantity: <span className="font-bold">{part?.quantity || 0}</span>
                            </Typography>
                        </div>
                        <div className="flex gap-2">
                            <Button
                                size="sm"
                                variant="outlined"
                                onClick={() => setShowTransactionForm(true)}
                            >
                                Record Movement
                            </Button>
                            <Button
                                size="sm"
                                variant="outlined"
                                disabled
                            >
                                <FontAwesomeIcon icon={faFileExport} className="mr-2" />
                                Export
                            </Button>
                        </div>
                    </div>

                    {summary && (
                        <Card className="mb-4 p-4 bg-gray-50">
                            <Typography variant="h6" className="mb-2">Summary</Typography>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                <div>
                                    <Typography variant="small" color="gray">Total Purchases</Typography>
                                    <Typography variant="h6">{formatCurrency(summary.total_purchases)}</Typography>
                                </div>
                                <div>
                                    <Typography variant="small" color="gray">Total Sales</Typography>
                                    <Typography variant="h6">{formatCurrency(summary.total_sales)}</Typography>
                                </div>
                                <div>
                                    <Typography variant="small" color="gray">Total Usage</Typography>
                                    <Typography variant="h6">{formatCurrency(summary.total_usage)}</Typography>
                                </div>
                                <div>
                                    <Typography variant="small" color="gray">Opening Qty</Typography>
                                    <Typography variant="h6">{summary.opening_quantity}</Typography>
                                </div>
                            </div>
                        </Card>
                    )}

                    <div className="mb-4 flex gap-2">
                        <Input
                            type="date"
                            label="Date From"
                            value={filters.date_from}
                            onChange={(e) => setFilters(prev => ({ ...prev, date_from: e.target.value }))}
                            className="max-w-xs"
                        />
                        <Input
                            type="date"
                            label="Date To"
                            value={filters.date_to}
                            onChange={(e) => setFilters(prev => ({ ...prev, date_to: e.target.value }))}
                            className="max-w-xs"
                        />
                    </div>

                    {loading ? (
                        <Typography>Loading...</Typography>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full table-auto border-collapse min-w-[900px]">
                                <thead>
                                    <tr className="bg-gray-100">
                                        <th className="border px-3 py-2 text-left text-xs sm:text-sm">Date</th>
                                        <th className="border px-3 py-2 text-left text-xs sm:text-sm">Type</th>
                                        <th className="border px-3 py-2 text-right text-xs sm:text-sm">Qty In</th>
                                        <th className="border px-3 py-2 text-right text-xs sm:text-sm">Qty Out</th>
                                        <th className="border px-3 py-2 text-right text-xs sm:text-sm">Unit Cost</th>
                                        <th className="border px-3 py-2 text-right text-xs sm:text-sm">Total Cost</th>
                                        <th className="border px-3 py-2 text-right text-xs sm:text-sm">Balance</th>
                                        <th className="border px-3 py-2 text-left text-xs sm:text-sm">Reference</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {stockCard.length === 0 ? (
                                        <tr>
                                            <td colSpan={8} className="border px-3 py-4 text-center text-sm text-gray-600">
                                                No transactions found
                                            </td>
                                        </tr>
                                    ) : (
                                        stockCard.map((trans) => (
                                            <tr key={trans.id} className="hover:bg-gray-50">
                                                <td className="border px-3 py-2 text-sm">{formatDate(trans.date)}</td>
                                                <td className="border px-3 py-2 text-sm">{getTransactionTypeLabel(trans.trans_type)}</td>
                                                <td className={`border px-3 py-2 text-sm text-right ${Number(trans.qty_in) > 0 ? 'text-green-600' : ''}`}>
                                                    {Number(trans.qty_in) > 0 ? trans.qty_in : '-'}
                                                </td>
                                                <td className={`border px-3 py-2 text-sm text-right ${Number(trans.qty_out) > 0 ? 'text-red-600' : ''}`}>
                                                    {Number(trans.qty_out) > 0 ? trans.qty_out : '-'}
                                                </td>
                                                <td className="border px-3 py-2 text-sm text-right">{trans.unit_cost ? formatCurrency(trans.unit_cost) : '-'}</td>
                                                <td className="border px-3 py-2 text-sm text-right">{trans.total_cost ? formatCurrency(trans.total_cost) : '-'}</td>
                                                <td className="border px-3 py-2 text-sm text-right font-bold">{trans.running_balance ?? trans.quantity_after}</td>
                                                <td className="border px-3 py-2 text-sm">
                                                    {trans.reference_type && trans.reference_id ? (
                                                        <span className="text-xs">{trans.reference_type}: {trans.reference_id}</span>
                                                    ) : '-'}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardBody>
            </Card>

            {showTransactionForm && (
                <PartsTransactionForm
                    open={showTransactionForm}
                    onClose={() => setShowTransactionForm(false)}
                    onSuccess={() => {
                        setShowTransactionForm(false);
                        fetchStockCard();
                    }}
                    partId={partId}
                />
            )}
        </div>
    );
};

export default PartsStockCard;
